import {
    calculateTime,
    removePunctuation,
    isLetterUpperCase,
    isWordUpperCase,
    hasPunctuation,
    hasDot,
  } from '../utils/functions';
  import {
    first,
    last,
    upperFirst,
    lowerFirst,
    sumBy,
    join,
    upperCase,
    toUpper,
    toLower,
    every,
    groupBy
  } from 'lodash';
  
  // ***************************************************************************************
  // addChar() appelée par l'application lors de l'appui sur une touche du clavier
  // correspondant à un nouveau caractère à insérer dans le script
  // ***************************************************************************************
  export function addChar(char) {
    if (
      this.readonly ||
      this.noCursor ||
      !this.currentWord ||
      (this.script.length && this.currentWord.submitted)
    ) {
      return;
    }
  
    this.typing = true;
  
    if (this.hasEmptyWord) {
      this.hasEmptyWord = false;
    }
  
    // Si le script est vide
    if (!this.script.length) {
      this.addNewActionForUndo('addChar', 0, 0, 1);
  
      const newWord = {
        index: 0,
        text: char,
        startTime: 0,
        endTime: 0,
        newline: false,
        color: '#FFFFFF',
        italic: false,
        markWord: 0,
        speakerId: null,
        cpbefore: false,
        cpafter: false,
        cpinside: false,
      };
  
      this.script = [newWord];
  
      this.currentWordIndex = 0;
      this.currentLineIndex = 0;
      this.currentLetterIndex = 1;
  
      this.onRefreshCanvas(true);
      this.onChange(0, 0);
  
      return;
    }
  
    // Si c'est en cours de sélection, on remplace les mots sélectionnés par un caractère
    if (this.underSelection) {

       if (this.lettersToSelect.length) {
        const nbWords = groupBy(this.lettersToSelect, (x) => x.indexWord);
        const keys = Object.keys(nbWords);
        const wordsSelected = this.script.filter((x) =>
          keys.includes(x.index.toString())
        );

        // On historise
        this.addNewActionOnUnderSelectionForUndo(
          'addChar',
          first(wordsSelected).index,
          wordsSelected,
          () => this.addChar(char)
        );

        // Si c'est un seul mot
        if (keys.length === 1) {
          const startLetter = first(this.lettersToSelect);
          const endLetter = last(this.lettersToSelect);

          const word = this.script[startLetter.indexWord];

          word.text =
            word.text.substring(0, startLetter.index) +
            char +
            word.text.substring(endLetter.index + 1);

          // Placer le curseur sur le mot
          this.currentWordIndex = word.index;
          this.currentLineIndex = word.indexLine;
          this.currentLetterIndex = startLetter.indexLetter + 1;

          this.onChange(word.startTime, word.index);
        } else {
          const wordsToDelete = [];
          const wordsToRegroup = [];

          for (const word of wordsSelected) {
            const lettersToDelete = this.lettersToSelect.filter(
              (x) => x.indexWord === word.index
            );

            if (lettersToDelete.length) {
              const startLetter = first(lettersToDelete);
              const endLetter = last(lettersToDelete);

              word.text =
                word.text.substring(0, startLetter.index) +
                word.text.substring(endLetter.index + 1);

              if (word.text.length === 0) {
                wordsToDelete.push(word);
              } else {
                wordsToRegroup.push(word);
              }
            }
          }

          if (wordsToDelete.length) {
            const firstIndex = first(wordsToDelete).index;

            // On supprime les mots de la liste
            this.script.splice(firstIndex, wordsToDelete.length);

            // si tous les mots ont été sélectionnés et supprimés
            if (wordsToDelete.length && !wordsToRegroup.length) {
              const firstWord = first(wordsToDelete);
              const lastWord = last(wordsToDelete);

              const newWord = {
                ...firstWord,
                text: char,
                endTime: lastWord.endTime,
              };

              this.script.splice(firstIndex, 0, newWord);

              // On place le curseur sur le mot
              this.currentWordIndex = newWord.index;
              this.currentLineIndex = newWord.indexLine;

              this.forceRecalculateLines = true;
              this.lettersToSelect = [];
              this.underSelection = false;

              this.onChange(newWord.startTime, newWord.index);

              return;
            }
          }

          if (wordsToRegroup.length) {
            const firstWord = first(wordsToRegroup);

            firstWord.text += char;

            this.currentWordIndex = firstWord.index;
            this.currentLineIndex = firstWord.indexLine;
            this.currentLetterIndex =
              firstWord.letters[firstWord.text.length]?.indexLetter;

            if (wordsToRegroup.length === 2) {
              const lastWord = last(wordsToRegroup);

              firstWord.endTime = lastWord.endTime;
              firstWord.text += lastWord.text;

              const indexToDelete = this.script.findIndex(
                (x) => x.index === lastWord.index
              );

              this.script.splice(indexToDelete, 1);
            }

            this.onChange(firstWord.startTime, firstWord.index);
          } else {
            // Prendre le précédent mot
            const firstWord = first(wordsSelected);
            if (firstWord) {
              let prevWord = this.script[firstWord.index - 1];

              // On place le curseur sur le précédent mot
              this.currentWordIndex = prevWord.index;
              this.currentLineIndex = prevWord.indexLine;
              this.currentLetterIndex = last(prevWord.letters).indexLetter;

              this.onChange(prevWord.startTime, prevWord.index);
            }
          }

          this.forceRecalculateLines = true;
        }

        this.lettersToSelect = [];
        this.underSelection = false;

        return;
      }

      const firstWord = first(this.wordsToSelect);
      const lastWord = last(this.wordsToSelect);
  
      // On historise
      this.addNewActionOnUnderSelectionForUndo(
        'addChar',
        firstWord.index,
        this.wordsToSelect,
        () => this.addChar(char)
      );
  
      this.script.splice(firstWord.index, this.wordsToSelect.length, firstWord);
  
      // On déselectionne
      this.underSelection = false;
      this.wordsToSelect = [];
  
      // On remplace l'ensemble des mots par le premier mot de la liste et on y met le caractère saisi
      firstWord.text = char;
  
      if (this.script.length > 1) {
        firstWord.endTime = lastWord.endTime;
      } else {
        // Parce qu'on a effacé tout le script, on doit réinitialiser le timecode
        firstWord.startTime = 0;
        firstWord.endTime = 0;
      }
  
      this.currentWordIndex = firstWord.index;
      this.currentLineIndex = firstWord.indexLine;
      this.currentLetterIndex = null;
  
      // Placer le curseur sur le mot
      this.onChange(firstWord.startTime, firstWord.index);
  
      return;
    }
      
    // si le char est un espace
    if (char === ' ') {
      this.addSpace();
      return;
    }
  
    // on ajoute dans l'historique des actions
    this.addNewActionForUndo('addChar', this.currentWord.index);
  
    const currentText = this.currentWord.text;
    const currentIndex = this.currentLetter.index;
  
    if (currentText === '') {
      const prevWord = this.script[this.currentWordIndex - 1];
      if (prevWord) {
        // si le mot précédent contient un point
        if (hasDot(prevWord.text) && !isLetterUpperCase(char)) {
          char = char.toUpperCase();
          
        }
      }
  
      this.currentWord.text = char;

      // si le char est un point alors, mettre la lettre suivante au majuscule
      if (char === '.' || char === '?' || char === "!") {
        const nextWord = this.script[this.currentWordIndex + 1];
        if (nextWord && nextWord.text && !isLetterUpperCase(nextWord.text[0])) {
          nextWord.text = upperFirst(nextWord.text);
        }
      }

      this.currentLetterIndex++;
      this.onChange(this.currentWord.startTime, this.currentWord.index);
      this.hasEmptyWord = false;
      return;
    }
     
    const startText = currentText.substring(0, currentIndex);
    const endText = currentText.substring(currentIndex);
  
    this.currentWord.text = startText + char + endText;
  
    // si le char est un point alors, mettre la lettre suivante au majuscule
    if (char === '.' || char === '?' || char === "!") {

      // si le dernier élément est un espace
      const lastLetter = this.currentWord.letters[this.currentWord.letters.length - 1];

      if (
        lastLetter &&
        this.currentLetter &&
        lastLetter.index === this.currentLetter.index
      ) {
        const nextWord = this.script[this.currentWordIndex + 1];
        if (nextWord && nextWord.text && !isLetterUpperCase(nextWord.text[0])) {
          nextWord.text = upperFirst(nextWord.text);
        }
      }
      else {
        this.currentWord.text =
          this.currentWord.text.substring(0, this.currentLetter.index + 1) +
          this.currentLetter.letter.toUpperCase() +
          this.currentWord.text.substring(this.currentLetter.index + 2);
      }
    }
  
    const letterWidth = this.backCtx.measureText(char).width;
  
    // Si c'est le dernier mot de la ligne et que le mot dépasse la fenêtre, on doit le mettre à la ligne
    if (this.currentWord.endX + letterWidth > this.width - this.metricX) {
      this.currentLetterIndex = startText.length + 1;
      this.currentLineIndex++;
      this.forceRecalculateLines = true;
      this.onChange(this.currentWord.startTime, this.currentWord.index);
      return;
    }
  
    this.currentLetterIndex++;
  
    this.onChange(this.currentWord.startTime, this.currentWord.index);
  }

// ***************************************************************************************
// addHebrewOrArabicChar()
// on ajoute les caractères en hébreu et en arabe de droite à gauche
// ***************************************************************************************
export function addHebrewOrArabicChar(char) {
  if (
    this.readonly ||
    this.noCursor ||
    !this.currentWord ||
    (this.script.length && this.currentWord.submitted)
  ) {
    return;
  }

  // on ajoute dans l'historique des actions
  this.addNewActionForUndo('addChar', this.currentWord.index);

  if (this.hasEmptyWord) {
    this.hasEmptyWord = false;
  }

  const currentText = this.currentWord.text;

  if (currentText === '') {
    this.currentWord.text = char;
    this.currentWord.rtl = true;

    this.onChange(this.currentWord.startTime, this.currentWord.index);
    return;
  }

  const currentIndex = this.currentLetter.index;

  const startIndex = currentText.length - currentIndex;

  const startText = currentText.substring(0, startIndex);
  const endText = currentText.substring(startIndex);
  this.currentWord.text = startText + char + endText;

  this.currentWord.rtl = true;

  const letterWidth = this.backCtx.measureText(char).width;

  // Si c'est le dernier mot de la ligne et que le mot dépasse la fenêtre, on doit le mettre à la ligne
  if (this.currentWord.endX + letterWidth > this.width - this.metricX) {
    this.currentLetterIndex = startText.length - 1;
    this.currentLineIndex++;
    this.forceRecalculateLines = true;
  }

  this.onChange(this.currentWord.startTime, this.currentWord.index);
}
  
  // ***************************************************************************************
  // addSpace permet d'ajouter un espace (soit en couper le mot en deux soit en créant un nouveau mot)
  // ***************************************************************************************
export function addSpace() {
  const currentIndex = this.currentWord.letters.findIndex(
    (l) => l.indexLetter === this.currentLetterIndex
  );
  
  if (this.currentWord.rtl) {
    this.addNewActionForUndo('addSpace', this.currentWord.index, 0, 1);

    // on créé un nouveau mot
    if (currentIndex === 0) {
      const newWord = {
        ...this.currentWord,
        text: '',
        letters: [],
        markWord: 0,
        cpbefore: false,
        cpafter: false,
        cpinside: false,
      };

      const prevWord = this.script[this.currentWord.index - 1];

      if (prevWord) {
        newWord.endTime = prevWord.endTime;
      } else {
        newWord.startTime = this.subtitle.startTime;
      }

      newWord.endTime = this.currentWord.startTime;

      this.script.splice(this.currentWord.index, 0, newWord);

      this.hasEmptyWord = true;

      this.onChange(this.currentWord.startTime, this.currentWord.index);

      return;
    }
  }

  const currentText = this.currentWord.text;

  let textWord1 = currentText.substring(0, currentIndex);
  let textWord2 = currentText.substring(currentIndex);

  if (this.currentWord.rtl) {
    const startIndex = currentText.length - currentIndex;

    textWord2 = currentText.substring(0, startIndex);
    textWord1 = currentText.substring(startIndex);
  }
  
  if (textWord1.length === 0) {
    return;
  }
  
  // on ajoute dans l'historique des actions
  this.addNewActionForUndo('addSpace', this.currentWord.index, 0, 1);
  
  // Si le mot est vide
  if (textWord2.length === 0) {
    const newWord = {
      ...this.currentWord,
      text: '',
      newline: false,
      letters: [],
      markWord: false,
      cpbefore: false,
      cpafter: false,
      cpinside: false,
    };
  
    const nextWord = this.script[this.currentWordIndex + 1];
    if (nextWord) {
      newWord.endTime = nextWord.startTime;
    } else {
      newWord.endTime = this.currentWord.endTime;
    }
  
    this.script.splice(this.currentWordIndex + 1, 0, newWord);
  
    this.currentWordIndex++;
    this.currentLetterIndex++;
  
    this.hasEmptyWord = true;
  
    this.onChange(this.currentWord.startTime, this.currentWord.index);
  
    return;
  }
  
  // Sinon on split le mot en deux
  const inOutTime = calculateTime(
    this.currentWord.startTime,
    this.currentWord.endTime,
    this.currentWord.text,
    textWord1
  );
  
  const word1 = {
    ...this.currentWord,
    text: textWord1,
    startTime: this.currentWord.startTime,
    endTime: inOutTime,
    letters: [],
  };
  
  const word2 = {
    ...this.currentWord,
    text: textWord2,
    newline: false,
    startTime: inOutTime,
    endTime: this.currentWord.endTime,
    letters: [],
    markWord: false,
    cpbefore: false,
    cpafter: false,
    cpinside: false,
  };
  
  this.script[this.currentWordIndex] = word1;
  this.script.splice(this.currentWordIndex + 1, 0, word2);
  
  this.currentWordIndex++;
  this.currentLetterIndex++;
  
  if (this.currentWord.parentIndexLine) {
    // calculer si le premier mot va à la ligne précédente pour garder mettre le curseur au début de la ligne
    const totalWidth = sumBy(
      this.script.filter((x) => x.indexLine === this.currentLineIndex - 1),
      (x) => x.width
    );
  
    const word1Width = this.backCtx.measureText(word1.text).width;
    if (totalWidth + word1Width <= this.width - this.metricX) {
      this.currentLetterIndex = 0;
    }
  }
  
  this.takeFirstLetter = true;
  this.onChange(this.currentWord.startTime, this.currentWord.index);
}
  
  // ***************************************************************************************
  // addHyphen permet d'ajouter le tiret
  // si le caractère est un tiret, alors on met un point dans le mot précédent
  // on met au majusucule le mot suivant
  // et le tiret est un nouveau mot
  // ***************************************************************************************
  export function addHyphen() {
    // on ajoute dans l'historique des actions
    if (this.currentWordIndex === 0) {
      this.addNewActionForUndo('addHyphen', this.currentWordIndex);
    } else {
      if (this.currentWord.newline) {
        this.addNewActionForUndo('addHyphen', this.currentWordIndex, 1, 2);
      } else {
        this.addNewActionForUndo('addHyphen', this.currentWordIndex - 1, 1, 2);
      }
    }
  
    let prevWord = null;
    if (!this.currentWord.newline) {
      prevWord = this.script[this.currentWordIndex - 1];
      if (prevWord) {
        prevWord.text = removePunctuation(prevWord.text) + '.';
      }
    }
  
    if (!isLetterUpperCase(this.currentWord.text[0])) {
      this.currentWord.text = upperFirst(this.currentWord.text);
    }
  
    const newWord = {
      ...this.currentWord,
      text: '-',
      newline: true,
      startTime: prevWord ? prevWord.endTime : this.currentWord.startTime,
      endTime: this.currentWord.startTime,
    };
  
    if (this.currentWord.newline) {
      this.currentWord.newline = false;
    }
  
    this.script.splice(this.currentWordIndex, 0, newWord);
    this.onChange(this.currentWord.startTime, this.currentWord.index);
  
    if (
      this.currentWord.y === 0 ||
      this.currentWord.y + this.lineHeight > this.height
    ) {
      this.scrollToDown(this.currentWord);
    }
  
    if (this.currentLetterIndex !== 0) {
      this.currentLineIndex++;
      this.onCanvasChange(true);
    }
  }
  
  // ***************************************************************************************
  // groupWordsWithoutSpace permet de regrouper les mots sans espace
  // ***************************************************************************************
  export function groupWordsWithoutSpace() {
    if (!this.underSelection) {
      this.groupWordByChar('groupWordsWithoutSpace', '', true);
      return;
    }
  
    const firstWord = first(this.wordsToSelect);
    const lastWord = last(this.wordsToSelect);
  
    // On historise
    this.addNewActionForUndo(
      'groupWordsWithoutSpace',
      firstWord.index,
      this.wordsToSelect.length,
      1
    );
  
    const sentence = join(
      this.wordsToSelect.map((x) => x.text),
      ''
    );
  
    const newWord = {
      ...firstWord,
      text: sentence,
      endTime: lastWord.endTime,
      letters: [],
    };
  
    this.currentWordIndex = firstWord.index;
    this.currentLetterIndex =
      first(firstWord.letters).indexLetter + sentence.length;
    this.underSelection = false;
  
    this.script.splice(firstWord.index, this.wordsToSelect.length, newWord);
    this.onChange(newWord.index, newWord.startTime);
  
    return;
  }
  
  // ***************************************************************************************
  // groupWordsByUnsecableSpace permet de regrouper les mots dans un espace insécable
  // ***************************************************************************************
  export function groupWordsByUnsecableSpace(next) {
    if (!this.underSelection) {
      this.groupWordByChar(
        'groupWordsByUnsecableSpace',
        String.fromCharCode(0xa0),
        next
      );
      return;
    }
  
    const firstWord = first(this.wordsToSelect);
    const lastWord = last(this.wordsToSelect);
  
    // On historise
    this.addNewActionForUndo(
      'groupWordsByUnsecableSpace',
      firstWord.index,
      this.wordsToSelect.length,
      1
    );
  
    const sentence = join(
      this.wordsToSelect.map((x) => x.text),
      String.fromCharCode(0xa0)
    );
  
    const newWord = {
      ...firstWord,
      text: sentence,
      endTime: lastWord.endTime,
      letters: [],
    };
  
    this.currentWordIndex = firstWord.index;
    this.currentLetterIndex =
      first(firstWord.letters).indexLetter + sentence.length;
    this.underSelection = false;
  
    this.script.splice(firstWord.index, this.wordsToSelect.length, newWord);
    this.onChange(newWord.index, newWord.startTime);
  }
  
  // ***************************************************************************************
  // onConversionToInitial permet de convertir le mot en un initial avec ou son espace
  // ***************************************************************************************
  export function onConversionToInitial(space) {
    if (!this.currentWord.text.length || this.currentWord.text === ' ') {
      return;
    }
  
    // On historise
    this.addNewActionForUndo(
      'onConversionToInitial',
      this.currentWord.index,
      space ? 1 : 2,
      1
    );
  
    this.currentWord.text = upperCase(this.currentWord.text[0]) + '.';
  
    if (!space) {
      const nextWord = this.script[this.currentWordIndex + 1];
      if (nextWord) {
        this.currentWord.endTime = nextWord.endTime;
        this.currentWord.text += nextWord.text;
  
        this.script.splice(nextWord.index, 1);
      }
    }
  
    this.onChange(this.currentWord.index, this.currentWord.startTime);
  }
  
  // ***************************************************************************************
  // onUpperOrLowerCase permet de mettre en majuscule ou en minuscule
  // ***************************************************************************************
  export function onUpperOrLowerCase(key) {
    const { text, startTime, index } = this.currentWord;
  
    // Si le texte contient un tiret comme le mot "deux-roues"
    // alors on doit faire en sorte de transformer le mot en "Deux-Roues" ou "deux-roues"
    if (key === 'UPPERCASEORLOWERCASEWITHDASH') {
      if (!text.includes('-')) {
        return;
      }
  
      this.addNewActionForUndo('onUpperOrLowerCase', index);
  
      const indexDash = text.indexOf('-');
  
      const firstPart = text.substring(0, indexDash);
      const secondPart = text.substring(indexDash + 1);
  
      const isUpperCase = isLetterUpperCase(text[0]);
  
      this.currentWord.text =
        (isUpperCase ? lowerFirst(firstPart) : upperFirst(firstPart)) +
        '-' +
        (isUpperCase ? lowerFirst(secondPart) : upperFirst(secondPart));
  
      this.onChange(index, startTime);
  
      return;
    }
  
    // Transformer le mot en majuscule
    if (key === 'UPPERCASEONWORD') {
      if (isWordUpperCase(text)) {
        return;
      }
  
      this.addNewActionForUndo('onUpperOrLowerCase', index);
      this.currentWord.text = text.toUpperCase();
  
      this.onChange(index, startTime);
  
      return;
    }
  
    // Transformer le mot en minuscule
    if (key === 'LOWERCASEONWORD') {
      if (text === text.toLowerCase()) {
        return;
      }
  
      this.addNewActionForUndo('onUpperOrLowerCase', index);
      this.currentWord.text = text.toLowerCase();
  
      this.onChange(index, startTime);
  
      return;
    }
  
    // Transformer la phrase en majuscule
    if (key === 'UPPERCASEORLOWERCASEONSENTENCE') {
      const indexLine =
        this.currentWord.parentIndexLine ?? this.currentWord.indexLine;
  
      const wordsToUpper = this.getWordsByIndexLine(indexLine);
  
      const firstWord = first(wordsToUpper);
  
      this.addNewActionForUndo(
        'onUpperOrLowerCase',
        firstWord.index,
        wordsToUpper.length,
        wordsToUpper.length
      );
  
      // Si toute la phrase est déjà au majuscule, le mettre tout au minuscule
      if (every(wordsToUpper, (x) => isWordUpperCase(x.text))) {
        wordsToUpper.forEach((word) => (word.text = word.text.toLowerCase()));
      } else {
        wordsToUpper.forEach((word) => (word.text = word.text.toUpperCase()));
      }
  
      // On met le curseur à la fin de la phrase
      this.currentWordIndex = last(wordsToUpper).index;
  
      this.forceRecalculateLines = true;
      this.onChange(firstWord.index, firstWord.startTime);
  
      return;
    }
  
    if (key === 'UPPERCASEORLOWERCASEWITHAPOSTROPHEORPERIOD') {
      const indexOfLetter = this.currentWord.letters.findIndex(
        (x) => x.letter === String.fromCharCode(39) || x.letter === '.'
      );
  
      if (indexOfLetter === -1 || indexOfLetter === text.length - 1) {
        return;
      }
  
      const letterIndexToUpperOrLower = indexOfLetter + 1;
  
      this.addNewActionForUndo('onUpperOrLowerCase', index);
  
      this.currentWord.text =
        text.substring(0, letterIndexToUpperOrLower) +
        (isLetterUpperCase(text[letterIndexToUpperOrLower])
          ? toLower(
              text.substring(
                letterIndexToUpperOrLower,
                letterIndexToUpperOrLower + 1
              )
            )
          : toUpper(
              text.substring(
                letterIndexToUpperOrLower,
                letterIndexToUpperOrLower + 1
              )
            )) +
        text.substring(letterIndexToUpperOrLower + 1);
  
      this.onChange(index, startTime);
  
      return;
    }
  
    // Mettre en majuscule ou en minuscule la première ou la deuxième lettre
    const firstLetter = key === 'UPPERCASEORLOWERCASEONFIRSTLETTER';
    if (!firstLetter && text.length < 2) {
      return;
    }
  
    this.addNewActionForUndo('onUpperOrLowerCase', index);
  
    if (firstLetter) {
      this.currentWord.text = isLetterUpperCase(text[0])
        ? lowerFirst(text)
        : upperFirst(text);
    } else {
      this.currentWord.text =
        text.substring(0, 1) +
        (isLetterUpperCase(text[1])
          ? toLower(text.substring(1, 2))
          : toUpper(text.substring(1, 2))) +
        text.substring(2);
    }
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // onReturnToLineWithHypen permet de mettre à la ligne avec un tiret
  // ***************************************************************************************
  export function onReturnToLineWithHypen() {
    const { text, startTime, index, newline } = this.currentWord;
  
    if (text.includes(String.fromCharCode(45))) {
      return;
    }
  
    // On historise
    this.addNewActionForUndo('onReturnToLineWithHypen', index - 1);
  
    if (!newline) {
      this.currentWord.newline = true;
      this.forceRecalculateLines = true;
    }
  
    const prevWord = this.script[index - 1];
    if (prevWord && !prevWord.text.includes('.')) {
      prevWord.text += '.';
    }
  
    this.currentWord.text = `${String.fromCharCode(45)} ${upperFirst(text)}`;
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // addPeriod permet d'ajouter un point à la fin du mot
  // si newline à true, alors je le mets à la ligne
  // ***************************************************************************************
  export function addPeriod(newline) {
    const { text, startTime, index } = this.currentWord;
  
    if (text.includes('.')) {
      return;
    }
  
    // On historise
    this.addNewActionForUndo('addPeriod', index);
  
    this.currentWord.text += '.';
  
    const nextWord = this.script[index + 1];
    if (nextWord && !isLetterUpperCase(nextWord.text)) {
      nextWord.text = upperFirst(nextWord.text);
    }
  
    if (newline) {
      nextWord.newline = true;
      this.forceRecalculateLines = true;
    }
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // removeUnsecableSpace permet de supprimer l'espace insécable
  // ***************************************************************************************
  export function removeUnsecableSpace() {
    const { index, text, startTime } = this.currentWord;
  
    if (text.includes(String.fromCharCode(0xa0)) || text.includes(' ')) {
      let texts = text.split(String.fromCharCode(0xa0));
  
      if (texts.length === 1) {
        texts = text.split(' ');
      }
  
      // On historise
      this.addNewActionForUndo('removeUnsecableSpace', index, 1, texts.length);
  
      const newWords = this.createWords(texts);
  
      this.script.splice(index, 1, ...newWords);
  
      this.onChange(index, startTime);
    }
  }
  
  // ***************************************************************************************
  // addQuotes permet d'ajouter des guillemets
  // ***************************************************************************************
  export function addQuotes() {
    const { index, startTime } = this.currentWord;
    let { text } = this.currentWord;
  
    if (this.underSelection) {
      const firstWord = first(this.wordsToSelect);
      const lastWord = last(this.wordsToSelect);
  
      // On historise
      this.addNewActionForUndo(
        'addQuotes',
        firstWord.index,
        this.wordsToSelect.length,
        this.wordsToSelect.length
      );
  
      firstWord.text = String.fromCharCode(34) + firstWord.text;
  
      let lastText = lastWord.text;
      let punctuation = '';
      if (hasPunctuation(lastText)) {
        lastText = removePunctuation(lastText);
        punctuation = lastWord.text.substring(lastText.length);
      }
  
      lastWord.text = lastText + String.fromCharCode(34) + punctuation;
  
      this.underSelection = false;
  
      this.onChange(firstWord.index, firstWord.startTime);
  
      return;
    }

    
    const hasHyphenWithSpace = text.startsWith('- ');
    const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

    if (hasHyphenWithSpace) {
      text = text.substring(2);
    } else if (hasHyphen) {
      text = text.substring(1);
    }
  
    if (
      text.startsWith(String.fromCharCode(34)) &&
      text.endsWith(String.fromCharCode(34))
    ) {
      return;
    }
  
    // On historise
    this.addNewActionForUndo('addQuotes', index);

    if (
    text.startsWith(String.fromCharCode(34)) ||
    text.endsWith(String.fromCharCode(34))
    ) {
      text = text.replace(String.fromCharCode(34), '');
    }
  
    let punctuation = '';
    if (hasPunctuation(text)) {
      text = removePunctuation(text);
      punctuation = this.currentWord.text.substring(text.length);
    }
  
    this.currentWord.text =
      String.fromCharCode(34) + text + String.fromCharCode(34) + punctuation;

    if (hasHyphenWithSpace) {
    this.currentWord.text = `- ${this.currentWord.text}`;
    } else if (hasHyphen) {
      this.currentWord.text = `-${this.currentWord.text}`;
    }
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // addApostrophes permet d'ajouter des apostrophes
  // ***************************************************************************************
  export function addApostrophes(one) {
    const { index, startTime } = this.currentWord;
    let { text } = this.currentWord;
  
    // on ajoute une apostrophe au mot
    if (one) {
      if (text.includes(String.fromCharCode(39))) {
        return;
      }
  
      // On historise
      this.addNewActionForUndo('addApostrophes', index);
  
      this.currentWord.text =
        text.substring(0, 1) + String.fromCharCode(39) + text.substring(1);
  
      this.currentLetterIndex++;
  
      this.onChange(index, startTime);
  
      return;
    }
  
    // on ajout les apostrophes au début et à la fin du mot
    if (this.underSelection) {
      const firstWord = first(this.wordsToSelect);
      const lastWord = last(this.wordsToSelect);
  
      // On historise
      this.addNewActionForUndo(
        'addApostrophes',
        firstWord.index,
        this.wordsToSelect.length,
        this.wordsToSelect.length
      );
  
      firstWord.text = String.fromCharCode(39) + firstWord.text;
  
      let lastText = lastWord.text;
      let punctuation = '';
      if (hasPunctuation(lastText)) {
        lastText = removePunctuation(lastText);
        punctuation = lastWord.text.substring(lastText.length);
      }
  
      lastWord.text = lastText + String.fromCharCode(39) + punctuation;
  
      this.underSelection = false;
  
      this.onChange(firstWord.index, firstWord.startTime);
  
      return;
    }

    const hasHyphenWithSpace = text.startsWith('- ');
    const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

    if (hasHyphenWithSpace) {
      text = text.substring(2);
    } else if (hasHyphen) {
      text = text.substring(1);
    }
  
    let punctuation = '';
    if (hasPunctuation(text)) {
      text = removePunctuation(text);
      punctuation = this.currentWord.text.substring(text.length);
    }
  
    // On historise
    this.addNewActionForUndo('addApostrophes', index);
  
    this.currentWord.text =
      String.fromCharCode(39) + text + String.fromCharCode(39) + punctuation;

    if (hasHyphenWithSpace) {
      this.currentWord.text = `- ${this.currentWord.text}`;
    } else if (hasHyphen) {
      this.currentWord.text = `-${this.currentWord.text}`;
    }
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // addOrRemoveLetter permet d'ajouter ou supprimer une lettre
  // ***************************************************************************************
  export function addOrRemoveLetter(key) {
    let letter;
    let { text } = this.currentWord;
  
    switch (key) {
      case 'ADDORREMOVESLETTER':
        if (text.endsWith('eau') || text.endsWith('eaux')) {
          letter = 'x';
          break;
        }
  
        letter = 's';
        break;
  
      case 'ADDORREMOVENTLETTER':
        letter = 'nt';
        break;
  
      default:
        return;
    }
  
    const { index, startTime } = this.currentWord;
  
    // On historise
    this.addNewActionForUndo('addOrRemoveLetter', index);
  
    let punctuation = '';
    if (hasPunctuation(text)) {
      text = removePunctuation(text);
      punctuation = this.currentWord.text.substring(text.length);
    }

    const hasDoubleQuotes = text.endsWith(String.fromCharCode(34));
    if (hasDoubleQuotes) {
      text = text.substring(0, text.length - 1);
    }
  
    const wordLetter = text.substring(text.length - letter.length);
  
    if (wordLetter === letter) {
      this.currentWord.text =
        text.substring(0, text.length - letter.length) + punctuation;
    } else {
      this.currentWord.text = text + letter + punctuation;
    }

    if (hasDoubleQuotes) {
      this.currentWord.text += String.fromCharCode(34);
    }
  
    this.currentLetterIndex = undefined;
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // invertPeriodAndComma permet d'inverser le point et la virgule
  // ***************************************************************************************
  export function invertPeriodAndComma() {
    if (!this.currentWord.letters.length) {
      return;
    }
  
    const { text, index, startTime } = this.currentWord;
  
    const letter = text.substring(text.length - 1);
  
    if (letter !== '.' && letter !== ',') {
      return;
    }
  
    // On historise
    this.addNewActionForUndo('invertPeriodAndComma', index);
  
    let letterToChange = letter === '.' ? ',' : '.';
  
    this.currentWord.text = text.replace(letter, letterToChange);
  
    // On met au majuscule ou au minuscule le prochain mot qui n'est pas à la ligne
    const nextWord = this.script[index + 1];
    if (nextWord && !nextWord.newline) {
      nextWord.text =
        letterToChange === '.'
          ? upperFirst(nextWord.text)
          : lowerFirst(nextWord.text);
    }
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // invertALetter permet d'inverser le a et à
  // ***************************************************************************************
  export function invertALetter() {
    const { text, index, startTime } = this.currentWord;
  
    if (text !== 'a' && text !== 'à') {
      return;
    }
  
    // On historise
    this.addNewActionForUndo('invertALetter', index);
  
    this.currentWord.text = text === 'a' ? 'à' : 'a';
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // hyphenateTwoWords permet de regrouper deux mots par un tiret
  // charCode 45 : Trait d'union - Signe moins
  // ***************************************************************************************
  export function hyphenateTwoWords() {
    if (!this.underSelection) {
      this.groupWordByChar('hyphenateTwoWords', String.fromCharCode(45), true);
      return;
    }
  
    if (this.wordsToSelect.length > 2) {
      return;
    }
  
    const firstWord = first(this.wordsToSelect);
  
    const lastWord = last(this.wordsToSelect);
  
    // On historise
    this.addNewActionForUndo('hyphenateTwoWords', firstWord.index, 2, 1);
  
    firstWord.text += String.fromCharCode(45) + lastWord.text;
    firstWord.endTime = lastWord.endTime;
  
    this.script.splice(lastWord.index, 1);
  
    this.underSelection = false;
    this.currentWordIndex = firstWord.index;
  
    this.onChange(firstWord.startTime, firstWord.index);
  }
  
  // ***************************************************************************************
  // replaceHyphenate permet de remplacer le tiret dans le mot par un espace ou un espace insécable
  // ***************************************************************************************
  export function replaceHyphenate(unsecableSpace) {
    const { text, index, startTime } = this.currentWord;
  
    if (!text.includes(String.fromCharCode(45))) {
      return;
    }
  
    if (unsecableSpace) {
      // On historise
      this.addNewActionForUndo('replaceHyphenate', index);
  
      this.currentWord.text = text.replace(
        String.fromCharCode(45),
        String.fromCharCode(0xa0)
      );
    } else {
      // On historise
      this.addNewActionForUndo('replaceHyphenate', index, 1, 2);
  
      const texts = this.currentWord.text.split(String.fromCharCode(45));
  
      const newWords = this.createWords(texts);
  
      this.script.splice(index, 1, ...newWords);
    }
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
// addOrRemoveLetterWithApostrophe permet d'ajouter ou d'enlever la lettre avec apostrophe
// Les lettres concernées : c, d, j, l, m, n, qu, s, t
// ***************************************************************************************
export function addOrRemoveLetterWithApostrophe(key) {
  const { index, startTime, newline } = this.currentWord;
  let { text } = this.currentWord;

  let letter = key[key.length - 1];

  if (letter === 'Q') {
    letter = 'qu';
  }

  const hasHyphenWithSpace = text.startsWith('- ');
  const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

  if (hasHyphenWithSpace) {
    text = text.substring(2);
  } else if (hasHyphen) {
    text = text.substring(1);
  }

  const hasDoubleQuotes = text.startsWith(String.fromCharCode(34));
  if (hasDoubleQuotes) {
    text = text.substring(1);
  }

  const letterWithApostrophe = letter.toLowerCase() + String.fromCharCode(39);

  this.addNewActionForUndo('addOrRemoveLetterWithApostrophe', index);

  if (text.toLowerCase().startsWith(letterWithApostrophe)) {
    this.currentWord.text = text.substring(letterWithApostrophe.length);
  } else {
    this.currentWord.text = letterWithApostrophe + text.toLowerCase();
  }

  if (hasDoubleQuotes) {
    this.currentWord.text = String.fromCharCode(34) + this.currentWord.text;
  }

  if (hasHyphenWithSpace) {
    this.currentWord.text = `- ${upperFirst(this.currentWord.text)}`;
  } else if (hasHyphen) {
    this.currentWord.text = `-${upperFirst(this.currentWord.text)}`;
  }

  const prevWord = this.script[index - 1];

  if (newline || (prevWord && prevWord.text.includes('.'))) {
    this.currentWord.text = upperFirst(this.currentWord.text);
  }

  this.onChange(index, startTime);
}

// ***************************************************************************************
// addOrRemoveLetterWithApostrophe permet d'ajouter ou d'enlever l'apostrophe sur la lettre
// Les lettres concernées : c, d, j, l, m, n, qu, s, t
// ***************************************************************************************
export function addOrRemoveApostropheToLetter(key) {
  const { index, startTime, newline } = this.currentWord;
  let { text } = this.currentWord;

  let letter = key[key.length - 1].toLowerCase();

  if (letter === 'q') {
    letter = 'qu';
  }

  const hasHyphenWithSpace = text.startsWith('- ');
  const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

  if (hasHyphenWithSpace) {
    text = text.substring(2);
  } else if (hasHyphen) {
    text = text.substring(1);
  }

  const hasDoubleQuotes = text.startsWith(String.fromCharCode(34));
  if (hasDoubleQuotes) {
    text = text.substring(1);
  }

  if (!text.toLowerCase().startsWith(letter)) {
    return;
  }

  this.addNewActionForUndo('addOrRemoveApostropheToLetter', index);

  const letterWithApostrophe = letter.toLowerCase() + String.fromCharCode(39);

  if (text.toLowerCase().includes(String.fromCharCode(39))) {
    this.currentWord.text = letter + text.substring(letter.length + 1);
  } else {
    this.currentWord.text =
      letterWithApostrophe + text.substring(letter.length);
  }

  if (hasDoubleQuotes) {
    this.currentWord.text = String.fromCharCode(34) + this.currentWord.text;
  }

  if (hasHyphenWithSpace) {
    this.currentWord.text = `- ${upperFirst(this.currentWord.text)}`;
  } else if (hasHyphen) {
    this.currentWord.text = `-${upperFirst(this.currentWord.text)}`;
  }

  const prevWord = this.script[index - 1];

  if (newline || (prevWord && prevWord.text.includes('.'))) {
    this.currentWord.text = upperFirst(this.currentWord.text);
  }

  this.onChange(index, startTime);
}
  
  // ***************************************************************************************
  // replaceLetter permet de remplacer les lettres
  // ***************************************************************************************
  export function replaceLetter(key) {
    let letters = [];
  
    switch (key) {
      case 'REPLACE_E':
        letters = [
          {
            containsLetter: [
              'ées',
              'ée',
              'eais',
              'eait',
              'eaient',
              'ais',
              'ait',
              'aient',
              'er',
              'ez',
            ],
            replaceLetter: 'é',
          },
          {
            containsLetter: ['é'],
            replaceLetter: 'er',
          },
        ];
        break;
      case 'REPLACE_R':
        letters = [
          {
            containsLetter: [
              'eais',
              'eait',
              'eaient',
              'ais',
              'ait',
              'aient',
              'é',
              'ez',
              'ai',
            ],
            replaceLetter: 'er',
          },
          {
            containsLetter: ['er'],
            replaceLetter: 'ez',
          },
        ];
        break;
      case 'REPLACE_Z':
        letters = [
          {
            containsLetter: ['r'],
            replaceLetter: 'z',
          },
          {
            containsLetter: [
              'eais',
              'eait',
              'eaient',
              'ais',
              'ait',
              'aient',
              'é',
              'ai',
            ],
            replaceLetter: 'ez',
          },
          {
            containsLetter: ['z'],
            replaceLetter: 'r',
          },
        ];
        break;
  
      case 'REPLACE_T':
        letters = [
          {
            containsLetter: ['é', 'ait'],
            replaceLetter: 'aient',
          },
          {
            containsLetter: ['aient'],
            replaceLetter: 'ait',
          },
        ];
        break;
  
      default:
        return;
    }
  
    const { index, startTime } = this.currentWord;
    let { text } = this.currentWord;
  
    let punctuation = '';
    if (hasPunctuation(text)) {
      text = removePunctuation(text);
      punctuation = this.currentWord.text.substring(text.length);
    }
  
    let lettersToDelete = '';
    let letterToReplace = '';
    for (const letter of letters) {
      const element = letter.containsLetter.find((suffixe) =>
        text.endsWith(suffixe)
      );
      if (element) {
        lettersToDelete = element;
        letterToReplace = letter.replaceLetter;
        break;
      }
    }
  
    if (!lettersToDelete && !letterToReplace) {
      return;
    }
  
    this.addNewActionForUndo('replaceLetter', index);
  
    this.currentWord.text =
      text.substring(0, text.length - lettersToDelete.length) +
      letterToReplace +
      punctuation;
  
    this.onChange(index, startTime);
  }
  
  // ***************************************************************************************
  // addOrRemoveLeToWord permet d'ajouter 'le' ou de l'enlever
  // ***************************************************************************************
  export function addOrRemoveLeToWord() {
    const { index, startTime } = this.currentWord;
    let { text } = this.currentWord;
  
    this.addNewActionForUndo('addOrRemoveLeToWord', index);
  
    let punctuation = '';
    if (hasPunctuation(text)) {
      text = removePunctuation(text);
      punctuation = this.currentWord.text.substring(text.length);
    }
  
    if (text.endsWith('le')) {
      this.currentWord.text = text.substring(0, text.length - 2) + punctuation;
    } else {
      this.currentWord.text = text + 'le' + punctuation;
    }
  
    this.onChange(index, startTime);
  }
  